import React, { useContext, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Avatar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import PeopleIcon from '@mui/icons-material/People';

//import Logo from "./Logo";
import { AuthContext } from "./Auth";
import { AppContext } from "./AppProvider";
import { useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { NavLink, useLocation } from "react-router-dom";
import AdminSubMenu from "./AdminSubMenu";
import AdminNestedList from "./AdminNestedList";

const ResponsiveAppBar = () => {
  const navigate = useNavigate();
  const { config } = useContext(AppContext);
  const pathnames = config.pathnames;
  const signInPath = pathnames.Login;
  const { authUser } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const location = useLocation();
  const isAdminPath = location.pathname.startsWith('/admin');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth).then(() => {
      document.location.href = "/";
    });
  };

  // Redirect to login with a return url parameter
  const redirectToLogin = (e) => {
    e.preventDefault();
    handleMenuClose?.();
    document.location.href = `${signInPath}?re=${document.location.pathname}${document.location.search}${document.location.hash}`;
  };

  const menuConfig = [
    { label: "Home", path: pathnames.Home, icon: <HomeIcon />, protected: false },
    { label: "Channels", path: pathnames.Channels, icon: <PeopleIcon />, protected: false },
    {
      label: "Dashboard",
      path: pathnames.Dashboard,
      icon: <DashboardIcon />,
      protected: true,
    },
  ];

  const filteredMenuItems = menuConfig.filter(
    (item) => !item.protected || authUser?.user
  );
  const drawerItems = [...filteredMenuItems];

  // On mobile with a logged-in user, add a divider and a Profile
  if (authUser?.user && isMobile) {
    drawerItems.push({ divider: true });
    drawerItems.push({ label: "Profile", path: pathnames.UserProfile, icon: <PersonIcon /> });
    if (authUser.hasOwnProperty('isAdmin') && authUser.isAdmin === true) {
      drawerItems.push({ label: "Admin", path: pathnames.Admin, icon: <DashboardIcon /> });
    }
  }

  const avatar =
    authUser?.user && (
      <Avatar
        alt={authUser.user.displayName}
        src={
          authUser.user.photoURL
            ? authUser.user.photoURL
            : `https://ui-avatars.com/api/?name=${encodeURIComponent(
                authUser.user.displayName
              )}&background=007bff&size=64&color=f8f9fc`
        }
        sx={{ width: 30, height: 30 }}
      />
    );

  const drawer = (
    <Box sx={{ width: 250 }} onClick={() => setDrawerOpen(false)}>
      {authUser?.user && (
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
          {avatar}
          <Typography variant="subtitle1" noWrap>
            {authUser.user.displayName}
          </Typography>
        </Box>
      )}
      <Divider sx={{ mb: 2 }} />
      <List>
        {drawerItems.map((item, index) => {
          if (item.divider)
            return <Divider key={`divider-${index}`} sx={{ my: 1 }} />;
          return (
            <ListItem button key={item.label} onClick={() => navigate(item.path)}>
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItem>
          );
        })}
        {isAdminPath &&
          <AdminNestedList />
        }
        {authUser?.user ? (
          <ListItem button onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItem>
        ) : (
          <ListItem button onClick={redirectToLogin}>
            <ListItemIcon>
              <LoginIcon />
            </ListItemIcon>
            <ListItemText primary="Login" />
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="fixed" sx={{ backgroundColor: "#0A1532" }}>
        <Toolbar sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          {isMobile ? (
            <>
              <IconButton edge="start" color="inherit" onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <div
                  style={{
                    height: 40,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                    <img src="/ccoo-logo.png" alt="CCOO Logo" style={{ height: '100%' }} />
                </div>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexGrow: 1 }}>
                <div
                  style={{
                    height: 50,
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                    <img src="/ccoo-logo.png" alt="CCOO Logo" style={{ height: '100%' }} />
                </div>
                {filteredMenuItems.map(({ label, path }) => (
                  <Typography
                    key={label}
                    variant="button"
                    onClick={() => navigate(path)}
                    sx={{ cursor: "pointer" }}
                  >
                    {label}
                  </Typography>
                ))}
                {isAdminPath &&
                  <AdminSubMenu />
                }
              </Box>
              {authUser?.user ? (
                <>
                  <IconButton onClick={handleMenuOpen} color="inherit">
                    {avatar}
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    id="user-menu"
                    keepMounted
                    open={isMenuOpen}
                    onClose={handleMenuClose}
                    slotProps={{
                      paper: {
                        elevation: 0,
                        sx: {
                          overflow: "visible",
                          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                          mt: 1.5,
                          "&::before": {
                            content: '""',
                            display: "block",
                            position: "absolute",
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: "background.paper",
                            transform: "translateY(-50%) rotate(45deg)",
                            zIndex: 0,
                          },
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  >
                    <MenuItem onClick={() => { handleMenuClose(); navigate(pathnames.UserProfile); }}>
                      <ListItemIcon>
                        <PersonIcon fontSize="small" />
                      </ListItemIcon>
                      Profile
                    </MenuItem>
                    {authUser.user && authUser.hasOwnProperty('isAdmin') && authUser.isAdmin === true && (
                      <MenuItem onClick={() => { handleMenuClose(); navigate(pathnames.Admin); }}>
                        <ListItemIcon>
                          <DashboardIcon fontSize="small" />
                        </ListItemIcon>
                        Admin
                      </MenuItem>
                    )}
                    <MenuItem onClick={() => { handleMenuClose(); handleLogout(); }}>
                      <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                      </ListItemIcon>
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Typography
                  variant="button"
                  onClick={redirectToLogin}
                  sx={{
                    cursor: 'pointer',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  Login
                </Typography>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        {drawer}
      </Drawer>
    </>
  );
};

export default ResponsiveAppBar;
